import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface NotificationFace {
  text: string;
  emoji: string;
  color: string;
}

const notifications: NotificationFace[] = [
  { text: "Welcome", emoji: "👋", color: "rgba(100, 108, 255, 0.8)" },
  { text: "Explore", emoji: "🚀", color: "rgba(0, 188, 212, 0.8)" },
  { text: "Create", emoji: "✨", color: "rgba(76, 175, 80, 0.8)" },
  { text: "Connect", emoji: "🔗", color: "rgba(255, 64, 129, 0.8)" },
  { text: "Innovate", emoji: "💡", color: "rgba(255, 193, 7, 0.8)" },
  { text: "Build", emoji: "🛠️", color: "rgba(156, 39, 176, 0.8)" }
];

const CubeContainer = styled(Box)({
  perspective: '1000px',
  width: '120px',
  height: '120px',
  position: 'relative',
  transformStyle: 'preserve-3d',
});

const CubeWrapper = styled(motion.div)({
  width: '100%',
  height: '100%',
  position: 'relative',
  transformStyle: 'preserve-3d',
  transition: 'transform 1s ease',
});

const Face = styled(Box)(({ color }: { color: string }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: '12px',
  border: `1px solid ${color}`,
  cursor: 'pointer',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
}));

const NotificationCube: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/submit-proposal');
  };

  return (
    <CubeContainer>
      <CubeWrapper
        animate={isHovered ? {
          rotateX: [0, 90, 180, 270, 360],
          rotateY: [0, 90, 180, 270, 360],
        } : {}}
        transition={isHovered ? {
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        } : {}}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={handleClick}
      >
        <Face
          color={notifications[0].color}
          sx={{
            transform: 'translateZ(60px)',
          }}
        >
          <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
            {notifications[0].text}
          </Typography>
          <Typography variant="h5">{notifications[0].emoji}</Typography>
        </Face>
        <Face
          color={notifications[1].color}
          sx={{
            transform: 'translateZ(-60px) rotateY(180deg)',
          }}
        >
          <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
            {notifications[1].text}
          </Typography>
          <Typography variant="h5">{notifications[1].emoji}</Typography>
        </Face>
        <Face
          color={notifications[2].color}
          sx={{
            transform: 'translateX(60px) rotateY(90deg)',
          }}
        >
          <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
            {notifications[2].text}
          </Typography>
          <Typography variant="h5">{notifications[2].emoji}</Typography>
        </Face>
        <Face
          color={notifications[3].color}
          sx={{
            transform: 'translateX(-60px) rotateY(-90deg)',
          }}
        >
          <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
            {notifications[3].text}
          </Typography>
          <Typography variant="h5">{notifications[3].emoji}</Typography>
        </Face>
        <Face
          color={notifications[4].color}
          sx={{
            transform: 'translateY(-60px) rotateX(90deg)',
          }}
        >
          <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
            {notifications[4].text}
          </Typography>
          <Typography variant="h5">{notifications[4].emoji}</Typography>
        </Face>
        <Face
          color={notifications[5].color}
          sx={{
            transform: 'translateY(60px) rotateX(-90deg)',
          }}
        >
          <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
            {notifications[5].text}
          </Typography>
          <Typography variant="h5">{notifications[5].emoji}</Typography>
        </Face>
      </CubeWrapper>
    </CubeContainer>
  );
};

export default NotificationCube; 